import { CircleAlert, CircleSlash, MinusCircle, PenLine, ShieldCheck, TriangleAlert } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { GroupHealth, GroupSummary, HealthOutcome } from '@/api/types'
import type { BadgeProps } from '@/components/ui/badge'

import { formatScore, HEALTH_REASONS } from './constants'

type Tone = NonNullable<BadgeProps['tone']>

/**
 * How a group's health reads on a screen.
 *
 * The one rule this whole file exists for: **a group that cannot be scored must never look like a
 * group that scored badly, and neither may look like a healthy one.** Three different facts, three
 * different appearances.
 *
 * The trap is a real one and the API contract spells it out. Two of the three inputs come from a
 * loan book that does not exist yet, so `OTR` and `PAR30` are unavailable rather than zero. A
 * group with no repayment history and a group that repaid everything on time both have "no missed
 * instalments"; a formula fed zeros scores the first as though it were the second, and a screen
 * that renders a missing score as `0` or as an empty cell does the same thing to a reader. So:
 *
 *   - never a blank cell,
 *   - never a zero standing in for an absent figure,
 *   - never a green tone on anything that was not actually scored and did not actually qualify.
 *
 * `qualifies` is the server's stored answer and is used as given. The threshold is configurable and
 * branch-specific, so a client comparing the score against a number of its own would eventually be
 * comparing it against the wrong one.
 */
export interface HealthPresentation {
  /** The short form for a table cell or a badge — a number, or a word that is plainly not one. */
  label: string
  tone: Tone
  icon: LucideIcon
  /** The outcome in words, for a heading or a title attribute. */
  outcomeLabel: string
  /** One sentence saying what the outcome means, and why when the API gave a reason. */
  explanation: string
  /** True only for `Scored` — the one case where `label` is a number. */
  isScored: boolean
  qualifies: boolean
  onWatch: boolean
}

const OUTCOME_LABELS: Record<HealthOutcome, string> = {
  Scored: 'Scored',
  InsufficientHistory: 'Not enough history',
  CreditDataUnavailable: 'Credit data unavailable',
  Disqualified: 'Disqualified',
  ProvisionalAttested: 'Attested',
}

const NEVER_COMPUTED: HealthPresentation = {
  label: 'Not assessed',
  tone: 'neutral',
  icon: MinusCircle,
  outcomeLabel: 'Never assessed',
  explanation:
    'Health has never been computed for this group, so there is no score to read — which is not the same as a score of nothing.',
  isScored: false,
  qualifies: false,
  onWatch: false,
}

/**
 * Reduces either shape the API serves — the flattened members on a list row, or the full
 * assessment on a detail — to the one presentation model both are rendered from, so the list and
 * the record can never disagree about what a group's health says.
 */
export function describeHealth(
  source:
    | Pick<GroupSummary, 'healthScore' | 'healthOutcome' | 'healthQualifies'>
    | { health: GroupHealth },
): HealthPresentation {
  const flattened = 'health' in source ? null : source
  const health = 'health' in source ? source.health : null

  const outcome: HealthOutcome | null = health ? health.outcome : (flattened?.healthOutcome ?? null)
  const score = health ? health.score : (flattened?.healthScore ?? null)
  const qualifies = health ? health.qualifies : (flattened?.healthQualifies ?? false)
  const onWatch = health?.onWatch ?? false
  const reason = health?.reason ?? null

  if (outcome === null) {
    return NEVER_COMPUTED
  }

  const explainReason = (fallback: string) =>
    (reason === null ? undefined : HEALTH_REASONS[reason]) ?? fallback

  switch (outcome) {
    case 'Scored': {
      // Defensive rather than expected: `Scored` without a number would be a contract violation,
      // and showing it as "Not assessed" is the only reading that cannot mislead.
      if (score === null) {
        return NEVER_COMPUTED
      }

      return {
        label: formatScore(score),
        tone: !qualifies ? 'danger' : onWatch ? 'warning' : 'success',
        icon: !qualifies ? CircleAlert : onWatch ? TriangleAlert : ShieldCheck,
        outcomeLabel: OUTCOME_LABELS.Scored,
        explanation: !qualifies
          ? 'Scored below the qualifying threshold, so the group does not qualify for another round.'
          : onWatch
            ? 'Above the qualifying threshold but below the watch threshold — it qualifies, and it is worth a closer look.'
            : 'Above both thresholds. The group qualifies for another round.',
        isScored: true,
        qualifies,
        onWatch,
      }
    }

    case 'Disqualified':
      return {
        // A zero here is a real answer rather than an absence, so the number is shown — with the
        // word beside it, because a bare `0.0` is exactly what a reader would take for a gap.
        label: `${formatScore(score ?? 0)} · disqualified`,
        tone: 'danger',
        icon: CircleAlert,
        outcomeLabel: OUTCOME_LABELS.Disqualified,
        explanation: explainReason(
          'A hard override applies to this group, so it is disqualified whatever the arithmetic says.',
        ),
        isScored: false,
        qualifies: false,
        onWatch: false,
      }

    case 'CreditDataUnavailable':
      return {
        label: 'Cannot be scored',
        tone: 'neutral',
        icon: CircleSlash,
        outcomeLabel: OUTCOME_LABELS.CreditDataUnavailable,
        explanation: explainReason(
          'The loan book cannot answer for this window, so two of the three inputs are unknown and no score can be produced.',
        ),
        isScored: false,
        qualifies: false,
        onWatch: false,
      }

    case 'InsufficientHistory':
      return {
        label: 'Cannot be scored',
        tone: 'neutral',
        icon: CircleSlash,
        outcomeLabel: OUTCOME_LABELS.InsufficientHistory,
        explanation: explainReason(
          'The window holds too little of this group’s life to score it, so there is no score rather than a low one.',
        ),
        isScored: false,
        qualifies: false,
        onWatch: false,
      }

    case 'ProvisionalAttested':
      // Qualification resting on a countersigned attestation, not a measurement. There is no
      // number and never will be one for this outcome; the warning tone plus the dashed edge keep
      // it from ever passing for a scored green — a person vouched, the arithmetic did not.
      return {
        label: 'Attested',
        tone: 'warning',
        icon: PenLine,
        outcomeLabel: OUTCOME_LABELS.ProvisionalAttested,
        explanation: explainReason(
          'A countersigned officer attestation stands in for a score the history cannot yet produce. It expires, and the group stays on watch until the window can be measured.',
        ),
        isScored: false,
        qualifies,
        onWatch: true,
      }

    default:
      // An outcome this build has never heard of. The server adds outcomes deliberately (its enum
      // says every screen must decide what to do with each one) — but a reader on an older build
      // must degrade to "no reading", not crash the whole groups screen.
      return NEVER_COMPUTED
  }
}
